// CharacterAvatar — renders a member's layered "space + cow" character avatar
// as a single circular SVG. Layers are drawn back-to-front:
//   backdrop → outfit (shoulders) → character (head) → hat.
// Pure and deterministic: same config → same drawing, no ids/gradients, so it
// is safe to render many at once (feed, comments, leaderboard).
import type { ReactElement } from "react";
import {
  type AvatarConfig,
  DEFAULT_AVATAR,
  renderBackdrop,
  renderCharacter,
  renderHat,
  renderOutfit,
} from "./catalog";

export interface CharacterAvatarProps {
  avatar: AvatarConfig;
  /** Rendered diameter in px. */
  size?: number;
  /** Accessible label (usually the member's name); title tooltip too. */
  title?: string;
  className?: string;
}

export function CharacterAvatar({
  avatar,
  size = 36,
  title,
  className,
}: CharacterAvatarProps): ReactElement {
  const cfg: AvatarConfig = { ...DEFAULT_AVATAR, ...avatar };
  return (
    <svg
      className={"char-avatar" + (className ? " " + className : "")}
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-label={title ? `${title}'s avatar` : "avatar"}
      style={{ borderRadius: "50%", display: "block", flex: "0 0 auto" }}
    >
      {title && <title>{`${title}'s avatar`}</title>}
      {/* The circular frame comes from CSS border-radius on the <svg> box
          (SVG content is overflow-clipped by default) — no shared clipPath id,
          so many avatars coexist on one page without id collisions. */}
      {renderBackdrop(cfg.backdrop)}
      {renderOutfit(cfg.outfit)}
      {renderCharacter(cfg.character)}
      {renderHat(cfg.hat)}
    </svg>
  );
}

export default CharacterAvatar;
